/* eslint-disable max-lines */
import classNames from 'classnames';
import {BonusPopup} from 'components/Header/BasicHeader/components/BonusPopup';
import style from 'components/Header/BasicHeader/style.module.scss';
import {IconElement} from 'components/IconElement';
import {Typography} from 'components/Typography';
import Link from 'components/CustomRoutingLink';
import React, {memo, useMemo, useState} from 'react';
import {CART_ROUTE} from 'core/modules/routing/constants';
import {useShowAuthDialog} from 'modules/auth/hooks/useShowAuthDialog';
import {useShowAuthDialogWithContinuation} from 'modules/auth/hooks/useShowAuthDialogWithContinuation';
import {Popup} from 'components/Popup';
import {HeaderUserMenu} from 'modules/user/components/HeaderUserMenu';
import {useIsCartPage} from 'modules/cart/hooks/useIsCartPage';
import {Button, BUTTON_SIZE, BUTTON_THEMES} from 'components/Button';
import {usePersonalPrivateRoutes} from 'core/modules/routing/hooks/private/usePersonalPrivateRoutes';
import {IDataAutotestProps} from 'core/types';
import {useGetCartQuery} from 'modules/cart/services';
import {CartHeaderPopup} from 'modules/cart/components/CartHeaderPopup';
import {CartAddNotification} from 'modules/cart/components/CartAddNotification';
import Icon from 'components/Icon';
import {useIsWorker} from 'core/hooks/useIsWorker';
import {useIsPrive} from 'core/hooks/useIsPrive';
import {useGetUserQuery} from 'modules/user/services';
import {MODALS} from 'modules/modals/constants';
import {Modal} from 'modules/modals/components/Modal';
import {PriveBonusesModal} from 'modules/modals/components/PriveBonusesModal';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {showModal} from 'modules/modals/thunks';
import {NEXT_PUBLIC_WINTER_DESIGN} from 'core/helpers/env';
import {MiniCartOpenGlobalState} from 'modules/cart/state/miniCartOpenGlobalState';

const UnAuthPopup = ({
    isProfilePopup,
    isBonusPopup,
    dataAutotestTarget,
    dataAutotestTargetId,
    title = 'Войдите, чтобы увидеть свои персональные скидки в каталоге',
    onShowAuthDialog,
}: {
    isProfilePopup?: boolean;
    title?: string;
    isBonusPopup?: boolean;
    onShowAuthDialog?: () => void;
} & IDataAutotestProps) => {
    const defaultHandleShowAuthDialog = useShowAuthDialog();
    const handleShowAuthDialog = onShowAuthDialog ?? defaultHandleShowAuthDialog;

    return (
        <div
            className={classNames(style.unAuthPopupContainer, {
                [style.profilePopup]: isProfilePopup,
            })}
        >
            <Typography
                data-auototest-target={dataAutotestTarget}
                data-auototest-target-id={`${dataAutotestTargetId}-text`}
                element={'div'}
                variant={isBonusPopup ? 'label-16-r' : 'label-16-m'}
            >
                {title}
            </Typography>
            <Button
                className={style.buttonAuth}
                data-auototest-target={dataAutotestTarget}
                data-auototest-target-id={`${dataAutotestTargetId}-btn`}
                onClick={handleShowAuthDialog}
                size={BUTTON_SIZE.small}
                theme={BUTTON_THEMES.primary}
            >
                Войти
            </Button>
        </div>
    );
};

type TBonusesButtonProps = {
    isLoggedIn: boolean;
    isWorker: boolean;
    isPrive: boolean;
    userLoyaltyPoints: number;
};

const BonusesButton = memo(({isLoggedIn, isWorker, isPrive, userLoyaltyPoints}: TBonusesButtonProps) => {
    const dispatch = useAppDispatch();

    if (isWorker) {
        return null;
    }

    const handleOpenPriveBonusesPopup = () => {
        dispatch(showModal(MODALS.PRIVE_BONUSES_MODAL.name));
    };

    return (
        <li className={style.headerLinkButton} onClick={isPrive ? handleOpenPriveBonusesPopup : undefined}>
            <Popup
                className={classNames(style.headerPopup, style.unAuthPopup)}
                delay={0}
                popup={
                    // eslint-disable-next-line no-nested-ternary
                    isLoggedIn ? (
                        !isPrive ? (
                            <BonusPopup
                                bonuses={userLoyaltyPoints}
                                dataAutotestTarget={'header-user-bonuses'}
                                dataAutotestTargetBtnId={'header-user-bonus-catalog'}
                                dataAutotestTargetLinkId={'header-user-bonus-loyalty'}
                                dataAutotestTargetTextId={'header-user-bonus-loyalty'}
                            />
                        ) : null
                    ) : (
                        <UnAuthPopup
                            dataAutotestTarget={'header-user-bonuses'}
                            dataAutotestTargetId={'header-user-bonuses-auth'}
                            isBonusPopup={true}
                            title={'Войдите, чтобы посмотреть баланс бонусных баллов.'}
                        />
                    )
                }
            >
                <div
                    className={'header-user-bonuses'}
                    data-autotest-target={'header-user'}
                    data-autotest-target-id={'header-user-bonuses'}
                >
                    <IconElement
                        className={classNames(style.headerIcon, style.headerIconBonus, {
                            [style.headerIconUnAuth]: !isLoggedIn || isWorker,
                        })}
                    >
                        {!isLoggedIn || isWorker ? (
                            <Icon icon={'bonuses'} />
                        ) : (
                            <div className={style.bonusesContainer}>
                                <div className={style.bonusesText}>{userLoyaltyPoints}</div>
                            </div>
                        )}
                        <Typography
                            className={classNames(style.headerLinkText, 'header-user-bonuses-text')}
                            data-autotest-target={'header-user-bonuses'}
                            data-autotest-target-id={'header-user-bonuses-text'}
                            element={'p'}
                            variant={'label-14-r-fluid'}
                        >
                            Бонусы
                        </Typography>
                    </IconElement>
                </div>
            </Popup>
        </li>
    );
});

type TFavoritesButtonProps = {
    isLoggedIn: boolean;
    favoritesUrl: () => string;
};

const FavoritesButton = memo(({isLoggedIn, favoritesUrl}: TFavoritesButtonProps) => {
    // NEW_YEAR_2026
    const [, setIsFavoritesHovered] = useState(false);
    return (
        <li className={style.headerLinkButton}>
            <Popup
                className={classNames(style.headerPopup, style.unAuthPopup)}
                delay={0}
                disable={isLoggedIn}
                popup={
                    <UnAuthPopup dataAutotestTarget={'header-user-fav'} dataAutotestTargetId={'header-user-fav-auth'} />
                }
            >
                <div
                    className={'header-user-fav'}
                    data-autotest-target={'header-user'}
                    data-autotest-target-id={'header-user-fav'}
                    // NEW_YEAR_2026
                    onMouseEnter={NEXT_PUBLIC_WINTER_DESIGN ? () => setIsFavoritesHovered(true) : undefined}
                    onMouseLeave={NEXT_PUBLIC_WINTER_DESIGN ? () => setIsFavoritesHovered(false) : undefined}
                >
                    <a
                        href={favoritesUrl()}
                        onClick={(e) => {
                            if (!isLoggedIn) {
                                e.preventDefault();
                            }
                        }}
                    >
                        <IconElement
                            className={classNames(style.headerIcon, {
                                [style.headerIconUnAuth]: !isLoggedIn,
                            })}
                        >
                            {/* {NEXT_PUBLIC_WINTER_DESIGN ? (*/}
                            {/*  <div className={style.iconWrapper}>*/}
                            {/*    <svg*/}
                            {/*      width="24"*/}
                            {/*      height="24"*/}
                            {/*      viewBox="0 0 24 24"*/}
                            {/*      fill="none"*/}
                            {/*      xmlns="http://www.w3.org/2000/svg"*/}
                            {/*      className={classNames(style.iconTransition, {*/}
                            {/*        [style.iconHidden]: isFavoritesHovered,*/}
                            {/*      })}*/}
                            {/*    >*/}
                            {/*      <path*/}
                            {/*        d="M11.8177 0L13.6236 7.03842L19.5311 2.80747L16.3903 9.36L23.6354 9.91622L17.0175 12.9169L22.21 18L15.2116 16.0447L15.9219 23.2763L11.8177 17.28L7.71345 23.2763L8.42377 16.0447L1.42538 18L6.6179 12.9169L-4.76837e-06 9.91622L7.24507 9.36L4.10424 2.80747L10.0118 7.03842L11.8177 0Z"*/}
                            {/*        fill="url(#paint0_linear_10213_5356)"*/}
                            {/*      />*/}
                            {/*      <defs>*/}
                            {/*        <linearGradient*/}
                            {/*          id="paint0_linear_10213_5356"*/}
                            {/*          x1="-1.38231"*/}
                            {/*          y1="11.5385"*/}
                            {/*          x2="6.78236"*/}
                            {/*          y2="25.9927"*/}
                            {/*          gradientUnits="userSpaceOnUse"*/}
                            {/*        >*/}
                            {/*          <stop stopColor="#DDDDDD" />*/}
                            {/*          <stop offset="0.576945" stopColor="#B7B7B7" />*/}
                            {/*          <stop offset="1" stopColor="#DDDDDD" />*/}
                            {/*        </linearGradient>*/}
                            {/*      </defs>*/}
                            {/*    </svg>*/}
                            {/*    <svg*/}
                            {/*      width="24"*/}
                            {/*      height="24"*/}
                            {/*      viewBox="0 0 24 24"*/}
                            {/*      fill="none"*/}
                            {/*      xmlns="http://www.w3.org/2000/svg"*/}
                            {/*      className={classNames(style.iconTransition, {*/}
                            {/*        [style.iconHidden]: !isFavoritesHovered,*/}
                            {/*      })}*/}
                            {/*    >*/}
                            {/*      <path*/}
                            {/*        d="M11.8177 0L13.6236 7.03842L19.5311 2.80747L16.3903 9.36L23.6354 9.91622L17.0175 12.9169L22.21 18L15.2116 16.0447L15.9219 23.2763L11.8177 17.28L7.71345 23.2763L8.42377 16.0447L1.42538 18L6.6179 12.9169L-4.76837e-06 9.91622L7.24507 9.36L4.10424 2.80747L10.0118 7.03842L11.8177 0Z"*/}
                            {/*        fill="url(#paint0_linear_10213_1816)"*/}
                            {/*      />*/}
                            {/*      <defs>*/}
                            {/*        <linearGradient*/}
                            {/*          id="paint0_linear_10213_1816"*/}
                            {/*          x1="19.7185"*/}
                            {/*          y1="25.4993"*/}
                            {/*          x2="12.7174"*/}
                            {/*          y2="0.846049"*/}
                            {/*          gradientUnits="userSpaceOnUse"*/}
                            {/*        >*/}
                            {/*          <stop stopColor="#6E85A4" />*/}
                            {/*          <stop offset="0.305266" stopColor="#2B3A50" />*/}
                            {/*          <stop offset="1" stopColor="#4D6483" />*/}
                            {/*        </linearGradient>*/}
                            {/*      </defs>*/}
                            {/*    </svg>*/}
                            {/*  </div>*/}
                            {/* ) : (*/}
                            <Icon icon={'favourite'} />
                            {/* )}*/}
                            <Typography
                                className={classNames(style.headerLinkText, 'header-user-fav-text')}
                                data-autotest-target={'header-user-fav'}
                                data-autotest-target-id={'header-user-fav-text'}
                                element={'p'}
                                variant={'label-14-r-fluid'}
                            >
                                Избранное
                            </Typography>
                        </IconElement>
                    </a>
                </div>
            </Popup>
        </li>
    );
});

type TCartButtonProps = {
    isCartPopupDisabled: boolean;
    productsCount: number;
    setIsMiniCartOpen: ReturnType<typeof MiniCartOpenGlobalState>['1'];
};

const CartButton = memo(({isCartPopupDisabled, productsCount, setIsMiniCartOpen}: TCartButtonProps) => {
    // NEW_YEAR_2026
    const [, setIsCartHovered] = useState(false);

    return (
        <>
            <li className={style.headerLinkButton}>
                <Popup
                    className={style.cartPopup}
                    delay={0}
                    disable={isCartPopupDisabled}
                    popup={<CartHeaderPopup />}
                    onOpenChange={(isOpen) => setIsMiniCartOpen(isOpen)}
                >
                    <div
                        className={'header-user-cart-link'}
                        data-autotest-target={'header-user'}
                        data-autotest-target-id={'header-user-cart'}
                        // NEW_YEAR_2026
                        onMouseEnter={NEXT_PUBLIC_WINTER_DESIGN ? () => setIsCartHovered(true) : undefined}
                        onMouseLeave={NEXT_PUBLIC_WINTER_DESIGN ? () => setIsCartHovered(false) : undefined}
                    >
                        <Link href={CART_ROUTE} legacyBehavior={true} passHref={true}>
                            <a
                                data-autotest-target={'header-user-cart'}
                                data-autotest-target-id={'header-user-cart-link'}
                            >
                                <IconElement className={style.headerIcon} count={productsCount}>
                                    {/* {NEXT_PUBLIC_WINTER_DESIGN ? (*/}
                                    {/*  <div className={style.iconWrapper}>*/}
                                    {/*    <svg*/}
                                    {/*      width="24"*/}
                                    {/*      height="24"*/}
                                    {/*      viewBox="0 0 24 24"*/}
                                    {/*      fill="none"*/}
                                    {/*      xmlns="http://www.w3.org/2000/svg"*/}
                                    {/*      className={classNames(style.iconTransition, {*/}
                                    {/*        [style.iconHidden]: isCartHovered,*/}
                                    {/*      })}*/}
                                    {/*    >*/}
                                    {/*      <path*/}
                                    {/*        fillRule="evenodd"*/}
                                    {/*        clipRule="evenodd"*/}
                                    {/*        d="M20.9142 3.69315V19.2473L16.8805 22.2858L3.77136 20.2509V3.69315L16.8805 1.71436L20.9142 3.69315ZM13.1238 4.9186C13.0976 6.77753 12.6958 8.21078 12.1268 9.20431C11.5353 10.2371 10.7858 10.7642 10.1079 10.8726C9.45261 10.9771 8.76003 10.7071 8.20356 9.9133C7.6387 9.10738 7.22599 7.76663 7.22505 5.81173L6.36958 5.94064C6.38812 7.93431 6.81517 9.42661 7.50127 10.4055C8.21182 11.4192 9.20935 11.884 10.2435 11.7188C11.2549 11.5571 12.1955 10.809 12.871 9.62953C13.5424 8.45679 13.9729 6.82615 13.9817 4.78885L13.1238 4.9186Z"*/}
                                    {/*        fill="url(#paint0_linear_9986_25585)"*/}
                                    {/*      />*/}
                                    {/*      <path*/}
                                    {/*        fillRule="evenodd"*/}
                                    {/*        clipRule="evenodd"*/}
                                    {/*        d="M20.9142 3.69315V19.2473L16.8805 22.2858L3.77136 20.2509V3.69315L16.8805 1.71436L20.9142 3.69315ZM13.1238 4.9186C13.0976 6.77753 12.6958 8.21078 12.1268 9.20431C11.5353 10.2371 10.7858 10.7642 10.1079 10.8726C9.45261 10.9771 8.76003 10.7071 8.20356 9.9133C7.6387 9.10738 7.22599 7.76663 7.22505 5.81173L6.36958 5.94064C6.38812 7.93431 6.81517 9.42661 7.50127 10.4055C8.21182 11.4192 9.20935 11.884 10.2435 11.7188C11.2549 11.5571 12.1955 10.809 12.871 9.62953C13.5424 8.45679 13.9729 6.82615 13.9817 4.78885L13.1238 4.9186Z"*/}
                                    {/*        fill="url(#paint1_linear_9986_25585)"*/}
                                    {/*      />*/}
                                    {/*      <defs>*/}
                                    {/*        <linearGradient*/}
                                    {/*          id="paint0_linear_9986_25585"*/}
                                    {/*          x1="2.91422"*/}
                                    {/*          y1="11.6045"*/}
                                    {/*          x2="10.5043"*/}
                                    {/*          y2="22.8021"*/}
                                    {/*          gradientUnits="userSpaceOnUse"*/}
                                    {/*        >*/}
                                    {/*          <stop stopColor="#D9E8FF" />*/}
                                    {/*          <stop offset="1" stopColor="#A9C2D1" />*/}
                                    {/*        </linearGradient>*/}
                                    {/*        <linearGradient*/}
                                    {/*          id="paint1_linear_9986_25585"*/}
                                    {/*          x1="2.91422"*/}
                                    {/*          y1="11.6045"*/}
                                    {/*          x2="10.5043"*/}
                                    {/*          y2="22.8021"*/}
                                    {/*          gradientUnits="userSpaceOnUse"*/}
                                    {/*        >*/}
                                    {/*          <stop stopColor="#DDDDDD" />*/}
                                    {/*          <stop offset="0.576945" stopColor="#B7B7B7" />*/}
                                    {/*          <stop offset="1" stopColor="#DDDDDD" />*/}
                                    {/*        </linearGradient>*/}
                                    {/*      </defs>*/}
                                    {/*    </svg>*/}
                                    {/*    <svg*/}
                                    {/*      width="24"*/}
                                    {/*      height="24"*/}
                                    {/*      viewBox="0 0 24 24"*/}
                                    {/*      fill="none"*/}
                                    {/*      xmlns="http://www.w3.org/2000/svg"*/}
                                    {/*      className={classNames(style.iconTransition, {*/}
                                    {/*        [style.iconHidden]: !isCartHovered,*/}
                                    {/*      })}*/}
                                    {/*    >*/}
                                    {/*      <path*/}
                                    {/*        fillRule="evenodd"*/}
                                    {/*        clipRule="evenodd"*/}
                                    {/*        d="M20.5714 3.69315V19.2473L16.5377 22.2858L3.42859 20.2509V3.69315L16.5377 1.71436L20.5714 3.69315ZM12.781 4.9186C12.7548 6.77753 12.353 8.21078 11.7841 9.20431C11.1925 10.2371 10.443 10.7642 9.76508 10.8726C9.10984 10.9771 8.41726 10.7071 7.86079 9.9133C7.29592 9.10738 6.88321 7.76663 6.88227 5.81173L6.0268 5.94064C6.04535 7.93431 6.4724 9.42661 7.1585 10.4055C7.86905 11.4192 8.86658 11.884 9.90069 11.7188C10.9121 11.5571 11.8527 10.809 12.5282 9.62953C13.1997 8.45679 13.6302 6.82615 13.639 4.78885L12.781 4.9186Z"*/}
                                    {/*        fill="url(#paint0_linear_9986_14149)"*/}
                                    {/*      />*/}
                                    {/*      <defs>*/}
                                    {/*        <linearGradient*/}
                                    {/*          id="paint0_linear_9986_14149"*/}
                                    {/*          x1="17.6434"*/}
                                    {/*          y1="23.5709"*/}
                                    {/*          x2="10.6713"*/}
                                    {/*          y2="3.11135"*/}
                                    {/*          gradientUnits="userSpaceOnUse"*/}
                                    {/*        >*/}
                                    {/*          <stop stopColor="#6E85A4" />*/}
                                    {/*          <stop offset="0.305266" stopColor="#2B3A50" />*/}
                                    {/*          <stop offset="1" stopColor="#4D6483" />*/}
                                    {/*        </linearGradient>*/}
                                    {/*      </defs>*/}
                                    {/*    </svg>*/}
                                    {/*  </div>*/}
                                    {/* ) : (*/}
                                    <Icon icon={'add-to-cart'} />
                                    {/* )}*/}
                                    <Typography
                                        className={classNames(style.headerLinkText, 'header-user-cart-text')}
                                        data-autotest-target={'header-user-cart'}
                                        data-autotest-target-id={'header-user-cart-text'}
                                        element={'p'}
                                        variant={'label-14-r-fluid'}
                                    >
                                        Корзина
                                    </Typography>
                                </IconElement>
                            </a>
                        </Link>
                    </div>
                </Popup>
                <CartAddNotification />
            </li>
        </>
    );
});

type TProfileButtonProps = {
    isLoggedIn: boolean;
    lk: () => string;
    userPhotoSrc: string;
    handleShowAuthDialog: () => void;
};

// eslint-disable-next-line max-lines-per-function
const ProfileButton = memo(({isLoggedIn, lk, userPhotoSrc, handleShowAuthDialog}: TProfileButtonProps) => {
    // NEW_YEAR_2026
    const [, setIsProfileHovered] = useState(false);

    return (
        <li className={style.headerLinkButton} role={'presentation'}>
            <Popup
                className={classNames(style.headerPopup, {
                    [style.unAuthPopup]: !isLoggedIn,
                })}
                delay={0}
                popup={
                    isLoggedIn ? (
                        <HeaderUserMenu />
                    ) : (
                        <UnAuthPopup
                            dataAutotestTarget={'header-user-auth'}
                            dataAutotestTargetId={'header-user-auth-popup'}
                            isProfilePopup={true}
                            onShowAuthDialog={handleShowAuthDialog}
                        />
                    )
                }
            >
                <div
                    className={classNames(style.headerUserAuth, 'header-user-auth')}
                    data-autotest-target={'header-user'}
                    data-autotest-target-id={'header-user-auth'}
                    // NEW_YEAR_2026
                    onMouseEnter={NEXT_PUBLIC_WINTER_DESIGN ? () => setIsProfileHovered(true) : undefined}
                    onMouseLeave={NEXT_PUBLIC_WINTER_DESIGN ? () => setIsProfileHovered(false) : undefined}
                >
                    {isLoggedIn ? (
                        <Link href={lk()}>
                            <IconElement className={style.headerIcon}>
                                {userPhotoSrc ? (
                                    <img alt={'user photo'} loading={'lazy'} src={userPhotoSrc} />
                                ) : (
                                    // : NEXT_PUBLIC_WINTER_DESIGN ?
                                    // <div className={style.iconWrapper}>
                                    //   <svg
                                    //     width="28"
                                    //     height="28"
                                    //     viewBox="0 0 28 28"
                                    //     fill="none"
                                    //     xmlns="http://www.w3.org/2000/svg"
                                    //     className={classNames(style.iconTransition, {
                                    //       [style.iconHidden]: isProfileHovered,
                                    //     })}
                                    //   >
                                    //     <path
                                    //       d="M14.0821 21.2C14.3294 22.36 15.4422 26 18.657 26C20.1408 26 21.8308 25.0001 22.738 23.6004C23.4797 22.4804 23.6446 21.2804 23.1501 20.2405C22.9852 19.8404 22.6966 19.5604 22.2845 19.4404C21.831 19.3204 21.2952 19.3204 20.7594 19.5204C19.9765 18.3202 19.4405 16.8402 19.1109 15.0402L23.2324 13.1602C24.9221 12.4401 25.2518 10.9601 24.8397 9.88013C24.4276 8.76 23.1911 7.92012 21.7073 8.40007L17.9979 9.91999C18.6572 8.9999 18.9458 7.7999 18.9458 6.79997C18.9458 4.19993 16.679 2 14 2C11.3209 2 9.05412 4.19993 9.05412 6.79997C9.05412 7.7999 9.3427 8.9999 10.0022 9.91965L6.29281 8.39973C4.80897 7.91977 3.57251 8.79984 3.16042 9.87979C2.74815 10.9599 3.07793 12.4398 4.76772 13.1598L8.88919 15.0398C8.55954 16.84 8.02376 18.3201 7.24064 19.5201C6.70468 19.32 6.16894 19.28 5.7156 19.4401C5.34453 19.5601 5.0149 19.8402 4.84999 20.2401C4.35528 21.28 4.52017 22.48 5.26209 23.6C6.16872 25.0001 7.85847 26 9.34231 26C12.5571 26 13.6698 22.36 13.9172 21.2H14.0821Z"
                                    //       fill="url(#paint0_linear_9986_14351)"
                                    //     />
                                    //     <defs>
                                    //       <linearGradient
                                    //         id="paint0_linear_9986_14351"
                                    //         x1="1.9"
                                    //         y1="13.5385"
                                    //         x2="10.4154"
                                    //         y2="27.3574"
                                    //         gradientUnits="userSpaceOnUse"
                                    //       >
                                    //         <stop stopColor="#DDDDDD" />
                                    //         <stop offset="0.576945" stopColor="#B7B7B7" />
                                    //         <stop offset="1" stopColor="#DDDDDD" />
                                    //       </linearGradient>
                                    //     </defs>
                                    //   </svg>
                                    //   <svg
                                    //     width="24"
                                    //     height="24"
                                    //     viewBox="0 0 24 24"
                                    //     fill="none"
                                    //     xmlns="http://www.w3.org/2000/svg"
                                    //   >
                                    //     <path
                                    //       d="M11.9995 1.7146C14.2958 1.7146 16.2388 3.60025 16.2388 5.82886C16.2387 6.68586 15.9913 7.71416 15.4263 8.50269L18.606 7.19995C19.8778 6.78856 20.9383 7.50839 21.2915 8.46851C21.6447 9.39414 21.3618 10.6628 19.9136 11.28L16.3804 12.8914C16.6629 14.4342 17.1224 15.7034 17.7935 16.7322C18.2527 16.5607 18.7124 16.56 19.1011 16.6628C19.4542 16.7657 19.7019 17.0056 19.8433 17.3484C20.2672 18.2398 20.1255 19.2693 19.4897 20.2292C18.7121 21.4288 17.2634 22.2859 15.9917 22.2859C13.2365 22.2859 12.2829 19.1663 12.0708 18.1716H11.9292C11.7171 19.1661 10.7626 22.2859 8.00732 22.2859C6.73555 22.2857 5.2873 21.4282 4.51025 20.2283C3.87443 19.2683 3.73272 18.2397 4.15674 17.3484C4.29807 17.0056 4.58093 16.7658 4.89893 16.6628C5.2875 16.5257 5.74715 16.5597 6.20654 16.7312C6.87767 15.7027 7.3371 14.4342 7.61963 12.8914L4.08643 11.28C2.63824 10.6629 2.35532 9.39429 2.7085 8.46851C3.06172 7.54283 4.12218 6.78856 5.39404 7.19995L8.57373 8.50269C8.00856 7.71446 7.76033 6.68586 7.76025 5.82886C7.76025 3.60039 9.70337 1.71483 11.9995 1.7146ZM11.9136 13.7146C11.4405 13.7148 11.0573 14.0979 11.0571 14.571C11.0571 15.0443 11.4404 15.4282 11.9136 15.4285C12.387 15.4285 12.771 15.0444 12.771 14.571C12.7708 14.0978 12.3869 13.7146 11.9136 13.7146ZM11.9136 11.1423C11.4404 11.1426 11.0572 11.5266 11.0571 11.9998C11.0571 12.473 11.4404 12.8569 11.9136 12.8572C12.387 12.8572 12.771 12.4731 12.771 11.9998C12.7709 11.5264 12.3869 11.1423 11.9136 11.1423Z"
                                    //       fill="url(#paint0_linear_9986_14147)"
                                    //     />
                                    //     <defs>
                                    //       <linearGradient
                                    //         id="paint0_linear_9986_14147"
                                    //         x1="18.208"
                                    //         y1="23.571"
                                    //         x2="11.7531"
                                    //         y2="2.73534"
                                    //         gradientUnits="userSpaceOnUse"
                                    //       >
                                    //         <stop stopColor="#6E85A4" />
                                    //         <stop offset="0.305266" stopColor="#2B3A50" />
                                    //         <stop offset="1" stopColor="#4D6483" />
                                    //       </linearGradient>
                                    //     </defs>
                                    //   </svg>
                                    // </div>
                                    <Icon icon={'profile'} />
                                )}
                                <Typography
                                    className={classNames(style.headerLinkText, 'header-user-auth-text')}
                                    data-autotest-target={'header-user-auth'}
                                    data-autotest-target-id={'header-user-auth-text'}
                                    element={'p'}
                                    variant={'label-14-r-fluid'}
                                >
                                    {'Профиль'}
                                </Typography>
                            </IconElement>
                        </Link>
                    ) : (
                        <button
                            className={'header-user-auth-btn'}
                            data-a11y-dialog-show={'auth-modal'}
                            data-autotest-target={'header-user-auth'}
                            data-autotest-target-id={'header-user-auth-btn'}
                            onClick={handleShowAuthDialog}
                        >
                            <IconElement className={style.headerIcon}>
                                {/* NEW_YEAR_2026*/}
                                {/* {NEXT_PUBLIC_WINTER_DESIGN ? (*/}
                                {/*  <div className={style.iconWrapper}>*/}
                                {/*    <svg*/}
                                {/*      width="24"*/}
                                {/*      height="24"*/}
                                {/*      viewBox="0 0 24 24"*/}
                                {/*      fill="none"*/}
                                {/*      xmlns="http://www.w3.org/2000/svg"*/}
                                {/*      className={classNames(style.iconTransition, {*/}
                                {/*        [style.iconHidden]: isProfileHovered,*/}
                                {/*      })}*/}
                                {/*    >*/}
                                {/*      <path*/}
                                {/*        d="M12.0704 18.1715C12.2823 19.1658 13.2361 22.2858 15.9917 22.2858C17.2635 22.2858 18.7121 21.4287 19.4897 20.229C20.1255 19.269 20.2668 18.2404 19.8429 17.349C19.7015 17.0061 19.4542 16.7662 19.101 16.6633C18.7123 16.5604 18.253 16.5604 17.7938 16.7319C17.1227 15.7031 16.6633 14.4345 16.3807 12.8917L19.9134 11.2802C21.3618 10.6631 21.6443 9.39443 21.2911 8.46875C20.9379 7.50864 19.8781 6.78874 18.6062 7.20013L15.4268 8.50292C15.9919 7.71427 16.2392 6.6857 16.2392 5.82861C16.2392 3.60001 14.2963 1.71436 11.9999 1.71436C9.70361 1.71436 7.76066 3.60001 7.76066 5.82861C7.76066 6.6857 8.00801 7.71427 8.57328 8.50262L5.39382 7.19984C4.12196 6.78845 3.06214 7.54279 2.70891 8.46846C2.35554 9.39428 2.63821 10.6628 4.0866 11.2799L7.61929 12.8914C7.33673 14.4344 6.87749 15.703 6.20624 16.7316C5.74685 16.5601 5.28764 16.5258 4.89907 16.663C4.58101 16.7659 4.29847 17.0059 4.15712 17.3487C3.73308 18.2401 3.87441 19.2687 4.51034 20.2287C5.28746 21.4287 6.73581 22.2858 8.00767 22.2858C10.7632 22.2858 11.717 19.1658 11.929 18.1715H12.0704Z"*/}
                                {/*        fill="url(#paint0_linear_9986_25589)"*/}
                                {/*      />*/}
                                {/*      <defs>*/}
                                {/*        <linearGradient*/}
                                {/*          id="paint0_linear_9986_25589"*/}
                                {/*          x1="1.62855"*/}
                                {/*          y1="11.6045"*/}
                                {/*          x2="8.92746"*/}
                                {/*          y2="23.4492"*/}
                                {/*          gradientUnits="userSpaceOnUse"*/}
                                {/*        >*/}
                                {/*          <stop stopColor="#DDDDDD" />*/}
                                {/*          <stop offset="0.576945" stopColor="#B7B7B7" />*/}
                                {/*          <stop offset="1" stopColor="#DDDDDD" />*/}
                                {/*        </linearGradient>*/}
                                {/*      </defs>*/}
                                {/*    </svg>*/}
                                {/*    <svg*/}
                                {/*      width="24"*/}
                                {/*      height="24"*/}
                                {/*      viewBox="0 0 24 24"*/}
                                {/*      fill="none"*/}
                                {/*      xmlns="http://www.w3.org/2000/svg"*/}
                                {/*      className={classNames(style.iconTransition, {*/}
                                {/*        [style.iconHidden]: !isProfileHovered,*/}
                                {/*      })}*/}
                                {/*    >*/}
                                {/*      <path*/}
                                {/*        d="M11.9995 1.7146C14.2958 1.7146 16.2388 3.60025 16.2388 5.82886C16.2387 6.68586 15.9913 7.71416 15.4263 8.50269L18.606 7.19995C19.8778 6.78856 20.9383 7.50839 21.2915 8.46851C21.6447 9.39414 21.3618 10.6628 19.9136 11.28L16.3804 12.8914C16.6629 14.4342 17.1224 15.7034 17.7935 16.7322C18.2527 16.5607 18.7124 16.56 19.1011 16.6628C19.4542 16.7657 19.7019 17.0056 19.8433 17.3484C20.2672 18.2398 20.1255 19.2693 19.4897 20.2292C18.7121 21.4288 17.2634 22.2859 15.9917 22.2859C13.2365 22.2859 12.2829 19.1663 12.0708 18.1716H11.9292C11.7171 19.1661 10.7626 22.2859 8.00732 22.2859C6.73555 22.2857 5.2873 21.4282 4.51025 20.2283C3.87443 19.2683 3.73272 18.2397 4.15674 17.3484C4.29807 17.0056 4.58093 16.7658 4.89893 16.6628C5.2875 16.5257 5.74715 16.5597 6.20654 16.7312C6.87767 15.7027 7.3371 14.4342 7.61963 12.8914L4.08643 11.28C2.63824 10.6629 2.35532 9.39429 2.7085 8.46851C3.06172 7.54283 4.12218 6.78856 5.39404 7.19995L8.57373 8.50269C8.00856 7.71446 7.76033 6.68586 7.76025 5.82886C7.76025 3.60039 9.70337 1.71483 11.9995 1.7146ZM11.9136 13.7146C11.4405 13.7148 11.0573 14.0979 11.0571 14.571C11.0571 15.0443 11.4404 15.4282 11.9136 15.4285C12.387 15.4285 12.771 15.0444 12.771 14.571C12.7708 14.0978 12.3869 13.7146 11.9136 13.7146ZM11.9136 11.1423C11.4404 11.1426 11.0572 11.5266 11.0571 11.9998C11.0571 12.473 11.4404 12.8569 11.9136 12.8572C12.387 12.8572 12.771 12.4731 12.771 11.9998C12.7709 11.5264 12.3869 11.1423 11.9136 11.1423Z"*/}
                                {/*        fill="url(#paint0_linear_9986_14147)"*/}
                                {/*      />*/}
                                {/*      <defs>*/}
                                {/*        <linearGradient*/}
                                {/*          id="paint0_linear_9986_14147"*/}
                                {/*          x1="18.208"*/}
                                {/*          y1="23.571"*/}
                                {/*          x2="11.7531"*/}
                                {/*          y2="2.73534"*/}
                                {/*          gradientUnits="userSpaceOnUse"*/}
                                {/*        >*/}
                                {/*          <stop stopColor="#6E85A4" />*/}
                                {/*          <stop offset="0.305266" stopColor="#2B3A50" />*/}
                                {/*          <stop offset="1" stopColor="#4D6483" />*/}
                                {/*        </linearGradient>*/}
                                {/*      </defs>*/}
                                {/*    </svg>*/}
                                {/*  </div>*/}
                                {/* ) : (*/}
                                <Icon icon={'profile'} />
                                {/* )}*/}
                                <Typography
                                    className={classNames(style.headerLinkText, 'header-user-auth-text')}
                                    data-autotest-target={'header-user-auth'}
                                    data-autotest-target-id={'header-user-auth-text'}
                                    element={'p'}
                                    variant={'label-14-r-fluid'}
                                >
                                    {'Войти'}
                                </Typography>
                            </IconElement>
                        </button>
                    )}
                </div>
            </Popup>
        </li>
    );
});

// eslint-disable-next-line complexity
export const HeaderRightButtons = memo(() => {
    const {data: user} = useGetUserQuery();

    const isLoggedIn = Boolean(user?.isAuthorized);
    const isWorker = useIsWorker();
    const isPrive = useIsPrive();

    const {data: cartData} = useGetCartQuery({mini: true});
    const cartProductsList = useMemo(() => cartData?.products || [], [cartData?.products]);

    const userPhotoSrc = user?.photo ?? '';

    const userLoyaltyPoints = user?.loyaltyPoints ?? 0;

    const isCartPage = useIsCartPage();

    // TODO(temp-test, seamless-auth): временная проводка continuation-ветки на кнопке "Войти" в хедере
    // для ручной проверки Шага 0/4 плана бесшовной авторизации (UC-112). Убрать после теста, вернуть useShowAuthDialog().
    const showAuthDialogWithContinuation = useShowAuthDialogWithContinuation();
    const handleShowAuthDialog = () =>
        showAuthDialogWithContinuation((freshUser) => {
            // eslint-disable-next-line no-console
            console.log('[seamless-auth test] continuation fired', freshUser);
        });

    const {
        favorites: {url: favoritesUrl},
        lk: {url: lk},
    } = usePersonalPrivateRoutes();

    const isCartPopupDisabled = isCartPage || 0 === cartProductsList.length;

    const productsCount = useMemo(() => {
        return cartProductsList.reduce((acc, item) => acc + (item?.cartData?.quantity || 0), 0);
    }, [cartProductsList]);

    const [, setIsMiniCartOpen] = MiniCartOpenGlobalState();

    return (
        <div className={style.headerBottomLinksItem}>
            <ul className={style.headerBottomLinks}>
                <BonusesButton
                    isLoggedIn={isLoggedIn}
                    isPrive={isPrive}
                    isWorker={isWorker}
                    userLoyaltyPoints={userLoyaltyPoints}
                />
                <FavoritesButton isLoggedIn={isLoggedIn} favoritesUrl={favoritesUrl} />
                <CartButton
                    isCartPopupDisabled={isCartPopupDisabled}
                    productsCount={productsCount}
                    setIsMiniCartOpen={setIsMiniCartOpen}
                />
                <ProfileButton
                    handleShowAuthDialog={handleShowAuthDialog}
                    isLoggedIn={isLoggedIn}
                    lk={lk}
                    userPhotoSrc={userPhotoSrc}
                />
            </ul>
            <Modal name={MODALS.PRIVE_BONUSES_MODAL.name} willMount={true} hasPortal={true}>
                <PriveBonusesModal />
            </Modal>
        </div>
    );
});
